import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useExitAnim } from "../../hooks/useExitAnim";
import { useSetterProof } from "../../hooks/useSetterProof";
import type { ProofMetricType, WorkSessionProof, IgnoredPerSessionProof } from "../../types/core";
import { formatMinutes } from "../../hooks/useSetterPerformance";
import { BucketSummary } from "./proof/BucketSummary";
import { getBucketColor } from "./proof/bucketColors";
import { ConversationProofList } from "./proof/ConversationProofList";
import { SessionProofTimeline } from "./proof/SessionProofTimeline";
import { IgnoredProofView } from "./proof/IgnoredProofView";
import { ProofGridView } from "./proof/ProofGridView";
import { UnengagedProofView } from "./proof/UnengagedProofView";
import type { ConversationNav } from "../../hooks/data-fetching/useConversationPreviews";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import {
  canComputeBucketsFromCache,
  computeProofBuckets,
  type CachedSetterBuckets,
} from "../../lib/proofBuckets";
import type { DailyRow } from "../../lib/aggregateDailyRows";
import {
  getDailyRowsCache,
  getSetterNamesCache,
  setDailyRowsCache,
  setSetterNamesCache,
} from "../../lib/setterPerformanceCacheService";
import { getDateKeyInTZ, toOrgStartISO, toOrgEndISO } from "../../lib/timezone";

interface SetterProofModalProps {
  isOpen: boolean;
  onClose: () => void;
  metricType: ProofMetricType;
  organizationId: string;
  setterIds: string[];
  dateRange: { start: Date | null; end: Date | null };
  timezone: string;
  onConversationClick: (
    conversationId: string,
    contactName: string,
    leadInstagram: string | null,
    highlightTimestamps?: { highlight: string; anchor?: string },
    nav?: ConversationNav
  ) => void;
  /** When 'window', BucketSummary for reply_turn_distribution shows in-window reply turns.
   * Omit or pass 'lifetime' for the default lifetime-total behavior. */
  defaultWindowMode?: "lifetime" | "window";
}

const METRIC_LABELS: Record<ProofMetricType, string> = {
  engaged_response: "Engaged Response",
  work_sessions: "Work Sessions",
  session_gap: "Session Gap",
  ignored_per_session: "Ignored Per Session",
  lead_reply_time: "Lead Reply Time",
  reply_turn_distribution: "Reply Turn Distribution",
  avg_reply_time: "Setter Reply Time",
  calls_pitched: "Calls Pitched",
  followup_persistence: "Follow-up Persistence",
  conversation_span: "Conversation Span",
  unengaged_conversations: "Unengaged Conversations",
  speed_to_lead: "Speed to Lead",
  unreplied: "Unreplied",
};

// Bucket ordering for each metric type
const BUCKET_ORDER: Record<string, string[]> = {
  engaged_response: ["<5min", "5-15min", "15min+"],
  session_gap: ["<30min", "30min-1hr", "1-2hr", "2hr+"],
  lead_reply_time: ["<5min", "5-30min", "30min-2hr", "2hr+"],
  reply_turn_distribution: [
    "19plus",
    "13_18",
    "11_12",
    "10",
    "9",
    "8",
    "7",
    "6",
    "5",
    "4",
    "3",
    "2",
    "1",
    "0",
  ],
  avg_reply_time: ["<30min", "30min-2hr", "2hr+", "ignored"],
  calls_pitched: ["replied", "no_reply"],
  followup_persistence: ["7+d", "7d", "6d", "5d", "4d", "3d", "2d", "1d"],
  conversation_span: ["2w+", "1-2w", "3-6d", "1-2d", "same_day"],
  unengaged_conversations: ["unengaged"],
  speed_to_lead: ["<5min", "5-30min", "30min-2hr", "2hr+", "pending", "no_reply"],
  unreplied: ["0_4h", "4_24h", "24_48h", "48h_plus"],
};

function formatDateRange(start: Date | null, end: Date | null): string {
  if (!start || !end) return "";
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return `${fmt(start)} — ${fmt(end)}`;
}

// Elapsed minutes from an ISO timestamp to now — feeds the generic
// formatMinutes() time_label fallback for "unreplied" rows, whose only
// timestamp is last_lead_msg_at (no reply_mins/response_mins field exists).
function minutesSinceTimestamp(iso: unknown): number | null {
  if (typeof iso !== "string" || !iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Number.isFinite(ms) ? ms / 60_000 : null;
}

export const SetterProofModal: React.FC<SetterProofModalProps> = ({
  isOpen,
  onClose,
  metricType,
  organizationId,
  setterIds,
  dateRange,
  timezone,
  onConversationClick,
  defaultWindowMode,
}) => {
  // Keep the modal mounted through the 200ms exit so anim-modal-out plays.
  const { mounted, closing } = useExitAnim(isOpen, 200);

  // windowMode controls whether BucketSummary for reply_turn_distribution shows
  // lifetime totals or in-window (date-range-scoped) reply turns.
  const [windowMode, setWindowMode] = useState<"lifetime" | "window">(
    defaultWindowMode ?? "lifetime"
  );

  // Reset to defaultWindowMode each time the modal opens so "Messaging → always Window /
  // ReplyTurns → always Lifetime" UX is preserved across re-opens.
  useEffect(() => {
    if (isOpen) setWindowMode(defaultWindowMode ?? "lifetime");
  }, [isOpen, defaultWindowMode]);

  // Window-mode bucket state — populated by the separate useEffect below when
  // metricType === 'reply_turn_distribution' and windowMode === 'window'.
  const [windowBuckets, setWindowBuckets] = useState<Array<{
    bucket: string;
    count: number;
    leadCount: number;
    color: string;
  }> | null>(null);
  const [windowLoading, setWindowLoading] = useState(false);
  const [windowError, setWindowError] = useState<string | null>(null);
  const { data, isLoading, error, fetchProof, clear } = useSetterProof({
    organizationId,
    setterIds,
    dateRange,
    timezone,
  });

  // Window-mode useEffect: fetches in-window reply-turn data per setter using
  // get_setter_per_lead_reply_turns_window when windowMode === 'window'.
  // Only fires for reply_turn_distribution; other metrics are unaffected.
  useEffect(() => {
    if (!isOpen || metricType !== "reply_turn_distribution" || windowMode !== "window") return;
    if (!organizationId || !dateRange?.start || !dateRange?.end || !supabase) return;
    // For multi-setter case: we fetch for the first setterId only; the renderSetterContent
    // call passes setterId as a single string, and the RPC accepts an array.
    // The branching in render handles per-setter bucketing by calling this effect once
    // per distinct setterId render path. Current design: one shared windowBuckets for the
    // modal's primary setter (setterIds[0]). Multi-setter window mode is not in scope.
    const primarySetterId = setterIds[0];
    if (!primarySetterId) return;

    let cancelled = false;
    setWindowBuckets(null);
    setWindowLoading(true);
    setWindowError(null);

    const endDate = new Date(dateRange.end);
    endDate.setHours(23, 59, 59, 999);

    (async () => {
      const { data: rpcData, error: rpcError } = await supabase!.rpc(
        "get_setter_per_lead_reply_turns_window",
        {
          p_organization_id: organizationId,
          p_setter_ids: setterIds,
          p_start_date: dateRange.start!.toISOString(),
          p_end_date: endDate.toISOString(),
        }
      );

      if (cancelled) return;
      if (rpcError) {
        setWindowError(rpcError.message);
        setWindowLoading(false);
        return;
      }

      // Bucket keys ordered DESCENDING (19+ at top, 0 at bottom) to match
      // the lifetime-mode row order users see on the same modal.
      const bucketKeys = [
        "19plus",
        "13_18",
        "11_12",
        "10",
        "9",
        "8",
        "7",
        "6",
        "5",
        "4",
        "3",
        "2",
        "1",
        "0",
      ] as const;
      const bucketMap = new Map<string, { count: number; leadCount: number }>();
      bucketKeys.forEach((k) => bucketMap.set(k, { count: 0, leadCount: 0 }));

      (rpcData ?? []).forEach((row: Record<string, unknown>) => {
        const n = Number(row.in_window_reply_turns ?? 0);
        const key =
          n === 0
            ? "0"
            : n === 1
              ? "1"
              : n === 2
                ? "2"
                : n === 3
                  ? "3"
                  : n === 4
                    ? "4"
                    : n === 5
                      ? "5"
                      : n === 6
                        ? "6"
                        : n === 7
                          ? "7"
                          : n === 8
                            ? "8"
                            : n === 9
                              ? "9"
                              : n === 10
                                ? "10"
                                : n >= 11 && n <= 12
                                  ? "11_12"
                                  : n >= 13 && n <= 18
                                    ? "13_18"
                                    : "19plus";
        const bucket = bucketMap.get(key)!;
        bucket.count += 1;
        if (row.last_message_from === "lead") bucket.leadCount += 1;
      });

      // Mirror the lifetime buckets shape: { bucket, count, leadCount, color }
      const result = bucketKeys.map((k) => ({
        bucket: k,
        count: bucketMap.get(k)!.count,
        leadCount: bucketMap.get(k)!.leadCount,
        color: getBucketColor(k),
      }));

      if (!cancelled) {
        setWindowBuckets(result);
        setWindowLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, metricType, windowMode, setterIds, organizationId, dateRange?.start, dateRange?.end]);

  // Reset active tab and grid view when metric changes or modal opens
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [gridFilter, setGridFilter] = useState<{ bucket: string; setterId: string } | null>(null);
  const [prevMetric, setPrevMetric] = useState(metricType);
  const [prevOpen, setPrevOpen] = useState(isOpen);

  // Adjust state when props change (React-recommended render-time pattern)
  if (metricType !== prevMetric || isOpen !== prevOpen) {
    setPrevMetric(metricType);
    setPrevOpen(isOpen);
    if (isOpen && activeTab !== null) {
      setActiveTab(null);
    }
    if (isOpen) {
      setGridFilter(null);
    }
  }

  // Bump to force cachedBuckets useMemo to recompute after cold-cache warming
  const [cacheGeneration, setCacheGeneration] = useState(0);

  // Compute instant bucket bars from cached daily rows (Phase 1)
  // Available before the RPC finishes — zero network, microseconds
  const cachedBuckets = useMemo<CachedSetterBuckets[] | null>(() => {
    // cacheGeneration is a dependency — forces recompute when cold-cache warming finishes
    void cacheGeneration;
    if (!isOpen || !organizationId || !canComputeBucketsFromCache(metricType)) return null;
    if (!dateRange.start || !dateRange.end) return null;

    const startStr = getDateKeyInTZ(dateRange.start.toISOString(), timezone);
    const endStr = getDateKeyInTZ(dateRange.end.toISOString(), timezone);
    const startMonth = startStr.slice(0, 7);
    const endMonth = endStr.slice(0, 7);
    const setterNames = getSetterNamesCache(organizationId);
    if (!setterNames) return null;

    // Collect daily rows for all months in the range
    const allRows: DailyRow[] = [];
    const months = new Set([startMonth, endMonth]);
    for (const m of months) {
      const monthRows = getDailyRowsCache<DailyRow[]>(organizationId, m);
      if (monthRows) allRows.push(...monthRows);
      else return null; // Not all months cached
    }

    if (allRows.length === 0) return null;
    return computeProofBuckets(allRows, startStr, endStr, metricType, setterNames, setterIds);
  }, [
    isOpen,
    organizationId,
    metricType,
    dateRange.start,
    dateRange.end,
    timezone,
    cacheGeneration,
    setterIds,
  ]);

  // Metrics where we use paginated per-bucket fetching instead of full dump
  const usePaginatedProof = cachedBuckets !== null && cachedBuckets.length > 0;

  // Cold-cache daily rows recovery: when a cacheable metric's daily rows aren't cached,
  // fetch them on-demand (~200ms) so we can compute buckets client-side.
  // Without this, cacheable metrics fall back to the full get_setter_proof RPC (8-15s timeout).
  const [isWarmingCache, setIsWarmingCache] = useState(false);
  useEffect(() => {
    if (!isOpen || usePaginatedProof || !canComputeBucketsFromCache(metricType)) return;
    if (!organizationId || !dateRange.start || !dateRange.end || !supabase) return;

    // For cacheable metrics with cold cache: fetch daily rows instead of full-fetch
    const startStr = getDateKeyInTZ(dateRange.start.toISOString(), timezone);
    const endStr = getDateKeyInTZ(dateRange.end.toISOString(), timezone);
    const months = new Set([startStr.slice(0, 7), endStr.slice(0, 7)]);
    const missingMonths: string[] = [];
    for (const m of months) {
      if (!getDailyRowsCache<DailyRow[]>(organizationId, m)) missingMonths.push(m);
    }

    const needsSetterNames = !getSetterNamesCache(organizationId);

    if (missingMonths.length === 0 && !needsSetterNames) return; // All cached — cachedBuckets should have computed

    let cancelled = false;
    setIsWarmingCache(true);

    (async () => {
      try {
        // Fetch missing daily rows
        for (const month of missingMonths) {
          const [year, mon] = month.split("-").map(Number);
          const lastDay = new Date(year, mon, 0).getDate(); // last day of month
          const { data } = await supabase!
            .from("setter_daily_performance")
            .select("*")
            .eq("organization_id", organizationId)
            .in("setter_id", setterIds)
            .gte("work_date", `${month}-01`)
            .lte("work_date", `${month}-${String(lastDay).padStart(2, "0")}`)
            .order("work_date", { ascending: true });

          if (!cancelled && data) {
            setDailyRowsCache(organizationId, month, data as DailyRow[]);
          }
        }

        // Fetch setter names if missing — required by cachedBuckets useMemo
        // TODO: migrate to useProfiles() — Phase 7.2 (docs/PLAN_rbac_security_scale_hardening.md)
        if (!cancelled && needsSetterNames) {
          const { data: profiles } = await supabase!
            .from("profiles")
            .select("ghl_user_id, full_name")
            .in("ghl_user_id", setterIds);

          if (!cancelled && profiles) {
            const nameMap: Record<string, string> = {};
            for (const p of profiles) {
              if (p.ghl_user_id && p.full_name) nameMap[p.ghl_user_id] = p.full_name;
            }
            if (Object.keys(nameMap).length > 0) {
              setSetterNamesCache(organizationId, nameMap);
            }
          }
        }
      } catch {
        // Fall through — fetchProof below will handle it
      } finally {
        if (!cancelled) {
          setIsWarmingCache(false);
          setCacheGeneration((g) => g + 1); // Force cachedBuckets useMemo to recompute
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    metricType,
    usePaginatedProof,
    organizationId,
    dateRange.start,
    dateRange.end,
    timezone,
    setterIds,
  ]);

  // Fetch proof data:
  // - Cacheable metrics with warm cache: skip (usePaginatedProof=true, per-bucket on click)
  // - Cacheable metrics warming cache: skip (isWarmingCache=true, will recompute when done)
  // - Non-cacheable/fast metrics: full fetch as before
  useEffect(() => {
    if (metricType === "unengaged_conversations") return;
    if (isOpen && !usePaginatedProof && !isWarmingCache) {
      // For cacheable metrics, only fire full-fetch as last resort (cache warming failed)
      if (canComputeBucketsFromCache(metricType)) {
        // Check one more time — cache may have been populated by the warming effect above
        // The useMemo for cachedBuckets will recompute on next render
        return;
      }
      fetchProof(metricType);
    }
    return () => clear();
  }, [isOpen, metricType, usePaginatedProof, isWarmingCache, fetchProof, clear]);

  // Lazy backfill: when cached buckets have partial data (e.g. avg_reply_time missing
  // "suffering" bucket), fire the full RPC in background to fill the gap. The bars show
  // instantly from cache; the missing bucket appears when the RPC resolves.
  useEffect(() => {
    if (metricType === "unengaged_conversations") return;
    if (!isOpen || !usePaginatedProof || !cachedBuckets) return;
    const hasPartial = cachedBuckets.some((sb) => sb.hasPartialData);
    if (!hasPartial) return;
    // Fire full RPC lazily — it will populate `data` which the render logic can merge
    fetchProof(metricType);
  }, [isOpen, usePaginatedProof, cachedBuckets, metricType, fetchProof]);

  // Per-bucket paginated fetch state
  const [bucketRows, setBucketRows] = useState<Record<string, unknown[]>>({});
  // S29: ids dismissed/killed in this session — filtered out of every ProofGridView
  // render so the cards vanish optimistically without refetching. The server-side
  // filter (applied via S29 dismiss-filter sweep) already excludes them on the
  // NEXT modal open, so this is a one-session UX patch.
  const [dismissedInSession, setDismissedInSession] = useState<Set<string>>(new Set());
  const { user } = useAuth();
  const currentUserGhlId = user?.ghlUserId ?? null;
  const handleAfterBulkDismiss = (ids: string[]) => {
    setDismissedInSession((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  };
  const [bucketLoading, setBucketLoading] = useState<string | null>(null);
  const [bucketError, setBucketError] = useState<string | null>(null);

  // Reset bucket state when modal opens/metric changes
  useEffect(() => {
    if (isOpen) {
      setBucketRows({});
      setBucketPages({});
      setBucketLoading(null);
      setBucketError(null);
    }
  }, [isOpen, metricType]);

  // S31 Step 5.7: clear per-bucket cache when windowMode flips for
  // reply_turn_distribution. Bucket membership is mode-dependent — a stale
  // cache entry would render the wrong rows. Only the bucketRows/bucketPages
  // for this metric matter; other metrics are unaffected by mode.
  useEffect(() => {
    if (metricType === "reply_turn_distribution") {
      setBucketRows({});
      setBucketPages({});
      setBucketLoading(null);
      setBucketError(null);
    }
  }, [windowMode, metricType]);

  // Progressive loading: fetch 9, show instantly, then grow to 18, then 27
  // Each batch appends smoothly — cards animate in via CSS, no layout flash
  const PAGE_SIZE = 9;
  const MAX_PAGES = 3;

  // Track how many pages we've loaded per bucket (to prevent re-fetching)
  const [bucketPages, setBucketPages] = useState<Record<string, number>>({});

  // Fetch rows for a specific bucket via paginated RPC.
  // Window-mode change invalidates the cache via the useEffect below that wipes
  // bucketRows/bucketPages — consumer cache keys stay 2-part.
  const fetchBucketRows = async (bucket: string, setterId: string, page = 0) => {
    if (!organizationId || !dateRange.start || !dateRange.end || !supabase) return;
    if (page >= MAX_PAGES) return;

    const cacheKey = `${setterId}:${bucket}`;
    const loadedPages = bucketPages[cacheKey] || 0;
    if (page < loadedPages) return; // Already have this page

    if (page === 0) {
      setBucketLoading(cacheKey);
      setBucketError(null);
    }

    // For reply_turn_distribution, thread windowMode so bucket membership and
    // the lifetime sidecar agree with the histogram. Other metrics ignore it.
    const modeKey = metricType === "reply_turn_distribution" ? windowMode : "lifetime";

    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc("get_setter_proof_rows", {
        p_organization_id: organizationId,
        p_setter_id: setterId,
        p_start_date: toOrgStartISO(dateRange.start, timezone),
        p_end_date: toOrgEndISO(dateRange.end, timezone),
        p_metric_type: metricType,
        p_bucket: bucket,
        p_limit: PAGE_SIZE,
        p_offset: page * PAGE_SIZE,
        p_window_mode: modeKey,
      });

      if (rpcError) throw rpcError;
      const newRows = (rpcData || []) as unknown[];

      setBucketRows((prev) => {
        const prevRows = (prev[cacheKey] || []) as unknown[];
        return { ...prev, [cacheKey]: page === 0 ? newRows : [...prevRows, ...newRows] };
      });
      setBucketPages((prev) => ({ ...prev, [cacheKey]: page + 1 }));

      // Auto-load next page after a delay (grows while user reads)
      if (newRows.length === PAGE_SIZE && page + 1 < MAX_PAGES) {
        setTimeout(() => {
          fetchBucketRows(bucket, setterId, page + 1);
        }, 1500); // 1.5s delay — enough time for first batch to settle visually
      }
    } catch (err) {
      console.warn("Failed to fetch bucket rows:", err);
      if (page === 0) {
        setBucketError(err instanceof Error ? err.message : "Failed to load conversations");
      }
    } finally {
      setBucketLoading(null);
    }
  };

  // ESC key handler — two-step:
  // 1st Escape: if a bucket drill-down is open (gridFilter !== null), pop back to the
  //             bucket list (clear the drill-down, modal stays open).
  // 2nd Escape: gridFilter is null → close the modal.
  //
  // Registered on the bubble phase (no { capture: true }) so that when
  // ConversationViewModal is stacked on top, its capture-phase listener fires first
  // and calls stopImmediatePropagation() — this handler never sees that event.
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (gridFilter !== null) {
          setGridFilter(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose, gridFilter]);

  // Type for proof data rows
  type ProofRow = { setter_id: string; setter_name: string; [key: string]: unknown };

  // Build flat conversation list for ProofGridView (grouped by conversation_id + setter_id + bucket)
  // Each entry aggregates ALL events for that conversation-bucket combo, with instance count
  // Memoized to prevent re-fetch loops — ProofGridView's useCallback depends on this reference
  // Must be before the early return to satisfy React hooks rules
  const gridConversations = useMemo(() => {
    if (!data) return [];

    // Group by conversation + setter + bucket to aggregate multiple events
    const groupMap = new Map<
      string,
      {
        conversation_id: string;
        contact_name: string | null;
        lead_instagram: string | null;
        bucket: string;
        setter_id: string;
        last_message_from: string | null;
        times: number[];
        highlightTimestamps: string[];
        anchorTimestamps: string[];
      }
    >();

    (data as unknown as ProofRow[]).forEach((row) => {
      const cid = row.conversation_id as string;
      const sid = row.setter_id as string;
      const bucket = (row.bucket as string) || "";
      if (!cid) return;

      // Key includes bucket so same conversation can appear in multiple buckets
      const key = `${cid}:${sid}:${bucket}`;

      const timeField = (
        metricType === "engaged_response" || metricType === "speed_to_lead"
          ? row.response_mins
          : metricType === "reply_turn_distribution"
            ? row.reply_turns
            : metricType === "calls_pitched"
              ? null
              : metricType === "followup_persistence"
                ? row.followup_days
                : metricType === "conversation_span"
                  ? row.span_days
                  : metricType === "unreplied"
                    ? minutesSinceTimestamp(row.last_lead_msg_at)
                    : row.reply_mins
      ) as number | null;

      const highlightTimestamp = (
        metricType === "engaged_response" || metricType === "speed_to_lead"
          ? row.setter_reply_at
          : metricType === "reply_turn_distribution"
            ? undefined
            : metricType === "calls_pitched"
              ? row.calendly_msg_at
              : metricType === "followup_persistence"
                ? undefined
                : metricType === "conversation_span"
                  ? undefined
                  : metricType === "unreplied"
                    ? row.last_lead_msg_at
                    : row.lead_reply_at
      ) as string | undefined;

      const anchorTimestamp = (
        metricType === "engaged_response" || metricType === "speed_to_lead"
          ? row.lead_msg_at
          : metricType === "reply_turn_distribution"
            ? undefined
            : metricType === "calls_pitched"
              ? row.calendly_msg_at
              : metricType === "followup_persistence"
                ? undefined
                : metricType === "conversation_span"
                  ? undefined
                  : metricType === "unreplied"
                    ? row.last_lead_msg_at
                    : row.setter_msg_at
      ) as string | undefined;

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          conversation_id: cid,
          contact_name: row.contact_name as string | null,
          lead_instagram: row.lead_instagram as string | null,
          bucket,
          setter_id: sid,
          last_message_from: (row.last_message_from as string) || null,
          times: [],
          highlightTimestamps: [],
          anchorTimestamps: [],
        });
      }

      const group = groupMap.get(key)!;
      if (timeField != null) group.times.push(timeField);
      if (highlightTimestamp) group.highlightTimestamps.push(highlightTimestamp);
      if (anchorTimestamp) group.anchorTimestamps.push(anchorTimestamp);
    });

    // Convert to flat array with aggregated data
    return Array.from(groupMap.values()).map((group) => {
      const avgTime =
        group.times.length > 0 ? group.times.reduce((a, b) => a + b, 0) / group.times.length : null;

      return {
        conversation_id: group.conversation_id,
        contact_name: group.contact_name,
        lead_instagram: group.lead_instagram,
        bucket: group.bucket,
        time_label:
          avgTime != null
            ? metricType === "reply_turn_distribution"
              ? `${Math.round(avgTime)} turns`
              : metricType === "followup_persistence"
                ? `${Math.round(avgTime)}d`
                : metricType === "conversation_span"
                  ? `${Math.round(avgTime)}d span`
                  : formatMinutes(avgTime)
            : "-",
        setter_id: group.setter_id,
        last_message_from: group.last_message_from,
        instanceCount: group.times.length,
        highlightTimestamps: group.highlightTimestamps,
        anchorTimestamps: group.anchorTimestamps,
      };
    });
  }, [data, metricType]);

  if (!mounted) return null;

  // Group data by setter — proof rows share setter_id/setter_name across all metric types
  const setterGroups = new Map<string, { name: string; items: ProofRow[] }>();
  if (data) {
    (data as unknown as ProofRow[]).forEach((row) => {
      if (!setterGroups.has(row.setter_id)) {
        setterGroups.set(row.setter_id, { name: row.setter_name, items: [] });
      }
      setterGroups.get(row.setter_id)!.items.push(row);
    });
  }

  const setterList = Array.from(setterGroups.entries());
  const useDesktopSideBySide = setterList.length === 2;
  const currentTab = activeTab || (setterList.length > 0 ? setterList[0][0] : null);

  // Render content for a specific setter
  const renderSetterContent = (setterId: string, setterName: string, items: ProofRow[]) => {
    if (metricType === "work_sessions") {
      return (
        <SessionProofTimeline
          sessions={items as unknown as WorkSessionProof[]}
          setterName={setterName}
          timezone={timezone}
        />
      );
    }

    if (metricType === "ignored_per_session") {
      return (
        <IgnoredProofView
          data={items as unknown as IgnoredPerSessionProof[]}
          setterName={setterName}
          timezone={timezone}
          onConversationClick={onConversationClick}
        />
      );
    }

    // Conversation-level metrics with buckets
    const bucketKeys = BUCKET_ORDER[metricType] || [];

    // Build bucket summary (with lead-last sub-counts for reply_turn_distribution)
    const bucketCounts = new Map<string, number>();
    const bucketLeadCounts = new Map<string, number>();
    bucketKeys.forEach((b) => {
      bucketCounts.set(b, 0);
      bucketLeadCounts.set(b, 0);
    });
    items.forEach((row) => {
      const b = row.bucket as string;
      bucketCounts.set(b, (bucketCounts.get(b) || 0) + 1);
      if (row.last_message_from === "lead") {
        bucketLeadCounts.set(b, (bucketLeadCounts.get(b) || 0) + 1);
      }
    });

    // For followup_persistence, hide empty buckets (dynamic display)
    const visibleBucketKeys =
      metricType === "followup_persistence" || metricType === "conversation_span"
        ? bucketKeys.filter((b) => (bucketCounts.get(b) || 0) > 0)
        : bucketKeys;

    const buckets = visibleBucketKeys.map((b) => ({
      bucket: b,
      count: bucketCounts.get(b) || 0,
      leadCount: bucketLeadCounts.get(b) || 0,
      color: getBucketColor(b),
    }));

    // Build grouped conversation list
    const groups = visibleBucketKeys.map((bucket) => {
      const convos = items
        .filter((row) => row.bucket === bucket)
        .map((row) => {
          const timeField = (
            metricType === "engaged_response" || metricType === "speed_to_lead"
              ? row.response_mins
              : metricType === "reply_turn_distribution"
                ? row.reply_turns
                : metricType === "calls_pitched"
                  ? null
                  : metricType === "followup_persistence"
                    ? row.followup_days
                    : metricType === "unreplied"
                      ? minutesSinceTimestamp(row.last_lead_msg_at)
                      : row.reply_mins
          ) as number | null;
          const timestampField = (
            metricType === "engaged_response" || metricType === "speed_to_lead"
              ? row.lead_msg_at
              : metricType === "reply_turn_distribution"
                ? ""
                : metricType === "calls_pitched"
                  ? row.calendly_msg_at
                  : metricType === "followup_persistence"
                    ? row.first_followup_date
                    : metricType === "conversation_span"
                      ? ""
                      : metricType === "unreplied"
                        ? row.last_lead_msg_at
                        : row.setter_msg_at
          ) as string;

          // Extract highlight + anchor timestamps for auto-scroll in conversation modal
          const highlightTimestamp = (
            metricType === "engaged_response" || metricType === "speed_to_lead"
              ? row.setter_reply_at
              : metricType === "reply_turn_distribution"
                ? undefined
                : metricType === "calls_pitched"
                  ? row.calendly_msg_at
                  : metricType === "followup_persistence"
                    ? undefined
                    : metricType === "unreplied"
                      ? row.last_lead_msg_at
                      : row.lead_reply_at
          ) as string | undefined;

          const anchorTimestamp = (
            metricType === "engaged_response" || metricType === "speed_to_lead"
              ? row.lead_msg_at
              : metricType === "reply_turn_distribution"
                ? undefined
                : metricType === "calls_pitched"
                  ? row.calendly_msg_at
                  : metricType === "followup_persistence"
                    ? undefined
                    : metricType === "unreplied"
                      ? row.last_lead_msg_at
                      : row.setter_msg_at
          ) as string | undefined;

          return {
            conversation_id: row.conversation_id as string,
            contact_name: row.contact_name as string | null,
            lead_instagram: row.lead_instagram as string | null,
            time_value: timeField,
            time_label:
              timeField != null
                ? metricType === "reply_turn_distribution"
                  ? `${timeField} turns`
                  : metricType === "followup_persistence"
                    ? `${timeField}d`
                    : formatMinutes(timeField)
                : "-",
            timestamp: timestampField,
            was_ignored: (row.was_ignored as boolean) || false,
            highlightTimestamp,
            anchorTimestamp,
          };
        });
      return { bucket, conversations: convos };
    });

    // For reply_turn_distribution in window mode: use windowBuckets when available,
    // else fall back to empty array while loading (least visual disruption — no skeleton flash
    // since the toggle already shows loading state via windowLoading).
    const activeBuckets =
      metricType === "reply_turn_distribution" && windowMode === "window"
        ? (windowBuckets ?? [])
        : buckets;

    return (
      <div className="space-y-4">
        {/* Window-mode loading indicator for reply_turn_distribution */}
        {metricType === "reply_turn_distribution" && windowMode === "window" && windowLoading && (
          <div className="flex items-center gap-2 py-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
            <span className="token-12 text-glass-muted">Loading window data...</span>
          </div>
        )}
        {metricType === "reply_turn_distribution" && windowMode === "window" && windowError && (
          <div className="bg-ds-red/10 border border-ds-red/30 rounded-lg p-3 text-ds-red token-12">
            {windowError}
          </div>
        )}
        <BucketSummary
          setterName={setterName}
          setterId={setterId}
          buckets={activeBuckets}
          totalCount={
            metricType === "reply_turn_distribution" && windowMode === "window"
              ? activeBuckets.reduce((sum, b) => sum + b.count, 0)
              : items.length
          }
          onBucketClick={(bucket, sid) => {
            const id = sid || setterId;
            // Always fetch via server-side RPC — client-side gridConversations filter
            // has edge cases where last_message_from doesn't match (stale data in full-fetch).
            // The SQL get_setter_proof_rows correctly parses _lead/_setter suffix.
            fetchBucketRows(bucket, id);
            setGridFilter({ bucket, setterId: id });
          }}
          windowMode={metricType === "reply_turn_distribution" ? windowMode : undefined}
          onWindowModeChange={metricType === "reply_turn_distribution" ? setWindowMode : undefined}
        />
        <div className="border-t border-black/[0.08] pt-4">
          <ConversationProofList
            groups={groups}
            timezone={timezone}
            onConversationClick={onConversationClick}
          />
        </div>
      </div>
    );
  };

  // Unreplied gets a per-setter header — this modal is always opened with a
  // single setterId for this metric (see SetterScorecard's onMetricClick), so
  // setterList[0] is the setter whose row was clicked. Falls back to the
  // generic "{Metric} Proof" header while data is still loading.
  const unrepliedHeaderSetter = metricType === "unreplied" ? setterList[0] : undefined;
  const unrepliedTitle = unrepliedHeaderSetter
    ? `${unrepliedHeaderSetter[1].name} · Unreplied`
    : undefined;
  const unrepliedSubtitle = unrepliedHeaderSetter
    ? `${unrepliedHeaderSetter[1].items.length} conversations with activity ${formatDateRange(
        dateRange.start,
        dateRange.end
      )} have no reply as of now`
    : undefined;

  return createPortal(
    <div
      className="fixed inset-0 z-[9998] flex items-start justify-center pt-8 pb-8"
      data-testid="proof-modal"
    >
      {/* Backdrop */}
      <div
        className={`${closing ? "anim-backdrop-out" : "anim-backdrop-in"} absolute inset-0 bg-black/50 backdrop-blur-sm`}
        onClick={onClose}
      />

      {/* Modal — anim-modal-in on the NON-glass wrapper (transform on detail-overlay = backdrop-filter repaint blur) */}
      <div
        className={`${closing ? "anim-modal-out" : "anim-modal-in"} w-full max-w-[85vw] mx-4 flex min-h-0`}
      >
        <div className="relative detail-overlay rounded-xl shadow-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.08] shrink-0">
            <div>
              <h2 className="token-18 text-glass">
                {unrepliedTitle ?? `${METRIC_LABELS[metricType]} Proof`}
              </h2>
              <p className="token-14 text-glass-muted">
                {unrepliedSubtitle ?? formatDateRange(dateRange.start, dateRange.end)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="anim-tap p-2 hover:bg-black/[0.05] rounded-lg transition-colors"
                aria-label="Close modal"
              >
                <svg
                  className="w-5 h-5 text-glass-muted"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6" style={{ overscrollBehavior: "contain" }}>
            {/* Paginated mode: instant cached bars + click-to-load per bucket */}
            {usePaginatedProof && !gridFilter && cachedBuckets && (
              <div>
                {cachedBuckets.length === 2 ? (
                  <div className="hidden lg:grid lg:grid-cols-2 lg:gap-8">
                    {cachedBuckets.map((sb) => (
                      <div key={sb.setterId}>
                        <BucketSummary
                          setterName={sb.setterName}
                          setterId={sb.setterId}
                          buckets={sb.buckets
                            .filter((b) => b.count > 0)
                            .map((b) => ({
                              bucket: b.bucket,
                              count: b.count,
                              color: getBucketColor(b.bucket),
                            }))}
                          totalCount={sb.totalCount}
                          onBucketClick={(bucket, sid) => {
                            const id = sid || sb.setterId;
                            fetchBucketRows(bucket, id);
                            setGridFilter({ bucket, setterId: id });
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  cachedBuckets.map((sb) => (
                    <div key={sb.setterId} className="mb-6">
                      <BucketSummary
                        setterName={sb.setterName}
                        setterId={sb.setterId}
                        buckets={sb.buckets
                          .filter((b) => b.count > 0)
                          .map((b) => ({
                            bucket: b.bucket,
                            count: b.count,
                            color: getBucketColor(b.bucket),
                          }))}
                        totalCount={sb.totalCount}
                        onBucketClick={(bucket, sid) => {
                          const id = sid || sb.setterId;
                          fetchBucketRows(bucket, id);
                          setGridFilter({ bucket, setterId: id });
                        }}
                      />
                    </div>
                  ))
                )}
                <p className="token-11 text-glass-muted mt-3">Click a bar to see conversations</p>
              </div>
            )}

            {/* Bucket drill-down view — renders when gridFilter is set AND either:
              (a) paginated mode (cached bars), or
              (b) server-side fetchBucketRows was called (full-fetch path with _lead/_setter suffix) */}
            {gridFilter &&
              (usePaginatedProof ||
                bucketRows[`${gridFilter.setterId}:${gridFilter.bucket}`] ||
                bucketLoading === `${gridFilter.setterId}:${gridFilter.bucket}`) && (
                <div>
                  <button
                    onClick={() => setGridFilter(null)}
                    className="anim-tap flex items-center gap-1 token-14 text-ds-accent hover:text-ds-accent mb-3 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    Back to buckets
                  </button>
                  {bucketLoading === `${gridFilter.setterId}:${gridFilter.bucket}` && (
                    <div className="flex items-center gap-2 py-8 justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                      <span className="token-14 text-glass-muted">Loading conversations...</span>
                    </div>
                  )}
                  {bucketError && (
                    <div className="bg-ds-red/10 border border-ds-red/30 rounded-lg p-3 text-ds-red token-14">
                      {bucketError}
                    </div>
                  )}
                  {bucketRows[`${gridFilter.setterId}:${gridFilter.bucket}`] && (
                    <ProofGridView
                      viewedSetterId={gridFilter.setterId}
                      currentUserGhlId={currentUserGhlId}
                      onAfterBulkDismiss={handleAfterBulkDismiss}
                      conversations={(
                        bucketRows[
                          `${gridFilter.setterId}:${gridFilter.bucket}`
                        ] as unknown as ProofRow[]
                      )
                        .filter(
                          (row) =>
                            !dismissedInSession.has(
                              (row as unknown as { conversation_id: string }).conversation_id
                            )
                        )
                        .map((row) => {
                          // Compute time_label based on metric type.
                          // For reply_turn_distribution, row.reply_turns is the WINDOW
                          // count when windowMode==='window' (per S31 Step 5.7 SQL)
                          // and lifetime count otherwise. row.lifetime_reply_turns is
                          // only populated by the window branch — used as sidecar.
                          const timeVal = (
                            metricType === "reply_turn_distribution"
                              ? row.reply_turns
                              : metricType === "engaged_response" || metricType === "speed_to_lead"
                                ? row.response_mins
                                : metricType === "calls_pitched"
                                  ? null
                                  : metricType === "followup_persistence"
                                    ? row.followup_days
                                    : metricType === "conversation_span"
                                      ? row.span_days
                                      : metricType === "ignored_per_session"
                                        ? row.ignore_count
                                        : metricType === "unreplied"
                                          ? minutesSinceTimestamp(row.last_lead_msg_at)
                                          : row.reply_mins
                          ) as number | null;

                          const isWindowReplyTurn =
                            metricType === "reply_turn_distribution" && windowMode === "window";

                          const timeLabel =
                            timeVal != null
                              ? metricType === "reply_turn_distribution"
                                ? isWindowReplyTurn
                                  ? `${timeVal} ${timeVal === 1 ? "reply" : "replies"}`
                                  : `${timeVal} turns`
                                : metricType === "followup_persistence"
                                  ? `${timeVal}d`
                                  : metricType === "conversation_span"
                                    ? `${timeVal}d span`
                                    : metricType === "ignored_per_session"
                                      ? `${timeVal}×`
                                      : formatMinutes(timeVal)
                              : "-";

                          // Lifetime sidecar — only relevant in Window mode so users see
                          // both the in-window count (drives bucket assignment) and the
                          // overall lifetime count of the conversation.
                          const lifetimeVal =
                            isWindowReplyTurn && row.lifetime_reply_turns != null
                              ? Number(row.lifetime_reply_turns)
                              : null;
                          const lifetimeLabel =
                            lifetimeVal != null ? `${lifetimeVal} lifetime` : undefined;

                          // Highlight/anchor timestamps vary by metric
                          const highlight = (
                            metricType === "engaged_response" || metricType === "speed_to_lead"
                              ? row.setter_reply_at
                              : metricType === "calls_pitched"
                                ? row.calendly_msg_at
                                : metricType === "reply_turn_distribution"
                                  ? undefined
                                  : metricType === "followup_persistence"
                                    ? undefined
                                    : metricType === "conversation_span"
                                      ? undefined
                                      : metricType === "unreplied"
                                        ? row.last_lead_msg_at
                                        : row.lead_reply_at
                          ) as string | undefined;

                          const anchor = (
                            metricType === "engaged_response" || metricType === "speed_to_lead"
                              ? row.lead_msg_at
                              : metricType === "calls_pitched"
                                ? row.calendly_msg_at
                                : metricType === "reply_turn_distribution"
                                  ? undefined
                                  : metricType === "followup_persistence"
                                    ? undefined
                                    : metricType === "conversation_span"
                                      ? undefined
                                      : metricType === "unreplied"
                                        ? row.last_lead_msg_at
                                        : row.setter_msg_at
                          ) as string | undefined;

                          return {
                            conversation_id: row.conversation_id as string,
                            contact_name: row.contact_name as string | null,
                            lead_instagram: row.lead_instagram as string | null,
                            bucket: row.bucket as string,
                            time_label: timeLabel,
                            lifetime_label: lifetimeLabel,
                            setter_id: row.setter_id as string,
                            instanceCount: 1,
                            highlightTimestamps: highlight ? [highlight] : [],
                            anchorTimestamps: anchor ? [anchor] : [],
                          };
                        })}
                      organizationId={organizationId}
                      timezone={timezone}
                      onConversationClick={onConversationClick}
                      activeDateRange={
                        dateRange.start && dateRange.end
                          ? { start: dateRange.start, end: dateRange.end }
                          : null
                      }
                    />
                  )}
                </div>
              )}

            {metricType === "unengaged_conversations" ? (
              <UnengagedProofView
                organizationId={organizationId}
                setterId={setterIds[0] ?? null}
                dateRange={dateRange}
                timezone={timezone}
                onConversationClick={onConversationClick}
              />
            ) : (
              <>
                {/* Original spinner: only for fast metrics without cached buckets */}
                {isLoading && !usePaginatedProof && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    <span className="ml-3 text-glass-muted">Loading proof data...</span>
                  </div>
                )}

                {error && !usePaginatedProof && (
                  <div className="bg-ds-red/10 border border-ds-red/30 rounded-lg p-4 text-ds-red token-14">
                    Error loading proof: {error}
                  </div>
                )}

                {!isLoading && !error && !usePaginatedProof && setterList.length === 0 && (
                  <p className="text-center text-glass-muted py-12">
                    No data for this metric in the selected period.
                  </p>
                )}

                {/* Full-fetch drill-down — only when bucketRows hasn't taken over (server-side filtering) */}
                {!isLoading &&
                  !error &&
                  setterList.length > 0 &&
                  gridFilter &&
                  metricType !== "work_sessions" &&
                  !bucketRows[`${gridFilter.setterId}:${gridFilter.bucket}`] &&
                  bucketLoading !== `${gridFilter.setterId}:${gridFilter.bucket}` && (
                    <div>
                      <button
                        onClick={() => setGridFilter(null)}
                        className="anim-tap flex items-center gap-1 token-14 text-ds-accent hover:text-ds-accent mb-3 transition-colors"
                        data-testid="proof-grid-toggle"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                        Back to buckets
                      </button>
                      <ProofGridView
                        viewedSetterId={gridFilter.setterId}
                        currentUserGhlId={currentUserGhlId}
                        onAfterBulkDismiss={handleAfterBulkDismiss}
                        conversations={gridConversations.filter((c) => {
                          if (dismissedInSession.has(c.conversation_id)) return false;
                          if (c.setter_id !== gridFilter.setterId) return false;
                          // Support _lead/_setter suffix for reply turn split bars
                          if (gridFilter.bucket.endsWith("_lead")) {
                            const depthBucket = gridFilter.bucket.slice(0, -5);
                            return (
                              c.bucket === depthBucket &&
                              (c as Record<string, unknown>).last_message_from === "lead"
                            );
                          }
                          if (gridFilter.bucket.endsWith("_setter")) {
                            const depthBucket = gridFilter.bucket.slice(0, -7);
                            return (
                              c.bucket === depthBucket &&
                              (c as Record<string, unknown>).last_message_from === "setter"
                            );
                          }
                          return c.bucket === gridFilter.bucket;
                        })}
                        organizationId={organizationId}
                        timezone={timezone}
                        onConversationClick={onConversationClick}
                        activeDateRange={
                          dateRange.start && dateRange.end
                            ? { start: dateRange.start, end: dateRange.end }
                            : null
                        }
                      />
                    </div>
                  )}

                {!isLoading && !error && setterList.length > 0 && !gridFilter && (
                  <>
                    {/* Desktop: side by side for 2 setters */}
                    {useDesktopSideBySide ? (
                      <div className="hidden lg:grid lg:grid-cols-2 lg:gap-8">
                        {setterList.map(([id, { name, items }]) => (
                          <div key={id}>{renderSetterContent(id, name, items)}</div>
                        ))}
                      </div>
                    ) : null}

                    {/* Mobile / 3+ setters / fallback: tabs */}
                    <div className={useDesktopSideBySide ? "lg:hidden" : ""}>
                      {setterList.length > 1 && (
                        <div className="flex gap-1 mb-4 bg-black/[0.05] rounded-lg p-1">
                          {setterList.map(([id, { name }]) => (
                            <button
                              key={id}
                              onClick={() => setActiveTab(id)}
                              className={`anim-tap flex-1 px-3 py-1.5 token-14 rounded-md transition-colors ${
                                currentTab === id
                                  ? "bg-black/[0.03] text-glass shadow-sm"
                                  : "text-glass-secondary hover:text-glass"
                              }`}
                            >
                              {name}
                            </button>
                          ))}
                        </div>
                      )}
                      {currentTab &&
                        setterGroups.has(currentTab) &&
                        renderSetterContent(
                          currentTab,
                          setterGroups.get(currentTab)!.name,
                          setterGroups.get(currentTab)!.items
                        )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
